import React, {createContext, useContext, useEffect, useState} from 'react';
import AuthService, {AuthInfo} from './AuthService';
import {Spinner} from '../common/spinner/spinner';
import {getRandomUserId} from '../settings/duck/settings-localstorage-service';
import {BACKEND_URL} from '../config';
import {beginKeycloakLogin, restorePendingAuthRecovery} from './auth-recovery';

let resolveGlobalAuthService: ((service: AuthService) => void) | null = null;
const globalAuthService: Promise<AuthService> = new Promise((resolve) => {
    resolveGlobalAuthService = resolve;
});

interface AuthContextValues {
    authService: AuthService | undefined | null;
    firstName: string;
    lastName: string;
    userId: string | null;
}

const defaultAuthContextValues: AuthContextValues = {
    authService: undefined,
    firstName: '',
    lastName: '',
    userId: null,
};

export const AuthContext = createContext<AuthContextValues>(defaultAuthContextValues);

interface AuthContextProviderProps {
    children: React.JSX.Element;
}

const createLocalAuthInfo = (enabled: boolean): AuthInfo => ({
    enabled,
    authenticated: false,
    userId: getRandomUserId(),
    firstName: 'N',
    lastName: 'A',
    preferredUsername: '',
});

const normalizeAuthInfo = (authInfo: AuthInfo): AuthInfo => {
    if (authInfo.enabled && authInfo.authenticated) {
        return {
            ...authInfo,
            firstName: authInfo.firstName || '',
            lastName: authInfo.lastName || '',
            preferredUsername: authInfo.preferredUsername || '',
        };
    }

    return createLocalAuthInfo(authInfo.enabled);
};

const publishGlobalAuthService = (service: AuthService) => {
    if (resolveGlobalAuthService) {
        resolveGlobalAuthService(service);
        resolveGlobalAuthService = null;
    }
};

const AuthContextProvider = (props: AuthContextProviderProps) => {
    const [authService, setAuthService] = useState<AuthService | null>(null);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [userId, setUserId] = useState<string | null>(null);

    useEffect(() => {
        const applyAuthInfo = (authInfo: AuthInfo) => {
            setFirstName(authInfo.firstName);
            setLastName(authInfo.lastName);
            setUserId(authInfo.userId);

            const service = new AuthService(authInfo);
            setAuthService(service);
            publishGlobalAuthService(service);
        };

        async function initialize() {
            try {
                const url = (BACKEND_URL || '') + '/auth/me';
                const response = await fetch(url, {credentials: 'include'});

                if (response.status === 401) {
                    beginKeycloakLogin();
                    return;
                }

                if (!response.ok) {
                    throw new Error(`Failed to fetch auth info: ${response.status}`);
                }

                const authInfo: AuthInfo = await response.json();

                if (authInfo.enabled && !authInfo.authenticated) {
                    beginKeycloakLogin();
                    return;
                }

                if (authInfo.enabled && authInfo.authenticated && restorePendingAuthRecovery()) {
                    return;
                }

                applyAuthInfo(normalizeAuthInfo(authInfo));
            } catch (error) {
                console.error('Failed to fetch auth info', error);
                applyAuthInfo(createLocalAuthInfo(false));
            }
        }

        void initialize();
    }, []);

    return (
        <AuthContext.Provider value={{authService, firstName, lastName, userId}}>
            {authService ? props.children : <Spinner />}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    return useContext(AuthContext);
};

export const getGlobalAuthService = () => globalAuthService;

export default AuthContextProvider;
